import {Box, Grid, InputAdornment, Typography, styled} from '@mui/material';
import type {SelectProps} from '..';
import {Checkbox, SelectIcon} from '../V1';
import {useEffect, useMemo, useState, useCallback} from 'react';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {SearchIcon} from '@jobseeker/lotus/icons';
import TextField from '@jobseeker/lotus/elements/Input';
import {Image} from '@jobseeker/lotus/components/Image';
import Masonry from '@mui/lab/Masonry';
import Transistion from '@mui/material/Fade';

const containsText = (text: string, searchText: string) =>
  text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

const ListBox = styled(Box)<any>(({theme, variant}: any) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  cursor: 'pointer',
  padding: theme.spacing(1, 2),
  ':hover': {
    backgroundColor: '#91B87929',
  },
  img: {
    marginTop: '3px',
  },
}));

export const Select = ({value, ...props}: SelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const SelectValue: any = value ?? [];

  const isGroup = useMemo(
    () => props.options?.some(option => (option.options?.length ?? 0) > 0),
    [props.options]
  );

  const SelectValueDisplay = useMemo(() => {
    if (!isGroup) return value;

    const allOptions = new Set<string | number>();
    props.options?.forEach(
      option =>
        option.options?.forEach(option2 => allOptions.add(option2.value))
    );

    return (value as any)?.filter((v: any) => allOptions.has(v));
  }, [value, isGroup, props.options]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.select-box-v3-lotus')) {
      setOpen(false);
    }
  }, []);

  const handleScroll = useCallback(() => setOpen(false), []);

  useEffect(() => {
    window?.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, {passive: true});

    return () => {
      window.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleClickOutside, handleScroll]);

  useEffect(() => {
    props.setMenuOpen?.(open ? props.name : false);
  }, [open, props.setMenuOpen, props.name]);

  const filteredOptions = useMemo(() => {
    if (!isGroup) {
      return props.options?.filter(option =>
        containsText(option.label.toString(), searchText)
      );
    }

    return props.options?.map(v => ({
      ...v,
      options: v.options?.filter(option =>
        containsText(option.label.toString(), searchText)
      ),
    }));
  }, [searchText, props.options, isGroup]);

  const handleSelect = useCallback(
    (e: any, value: string | number) => {
      const alreadySelected = SelectValue?.includes(value);
      const finalData = alreadySelected
        ? SelectValue?.filter((c: any) => c !== value)
        : [...SelectValue, value];

      props.onChange?.(
        {...e, target: {...e.target, name: props.name, value: finalData}},
        props.multiple ? finalData : [value]
      );
      setSearchText('');
    },
    [SelectValue, props.onChange, props.name, props.multiple]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target?.value);
    },
    []
  );

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    } else {
      e.stopPropagation();
    }
  }, []);

  const toggleOpen = useCallback(() => setOpen(prev => !prev), []);

  return (
    <Box className="select-box-v3-lotus">
      <Box
        sx={{
          border: '1px solid',
          borderColor: '#7E7E7E33',
          cursor: 'pointer',
          '&:focus': {
            borderRadius: 25,
            borderColor: '#33333390',
          },
          '&:hover': {
            borderRadius: 25,
            borderColor: '#33333390',
          },
        }}
        px={1.5}
        py={1}
        pr={{sm: 2.2}}
        borderRadius={25}
        width={'100%'}
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        onClick={toggleOpen}
      >
        <Typography
          fontWeight={500}
          color={'text.primary'}
          lineHeight={'21.8px'}
          variant="subtitle2"
        >
          {SelectValueDisplay?.length
            ? `${SelectValueDisplay.length} Selected`
            : props.label}
        </Typography>
        <SelectIcon
          open={open}
          sx={{
            margin: 0,
            transform: open ? 'rotate(180deg)' : 'unset',
            transistion: 'none !important',
          }}
        />
      </Box>
      <Transistion in={open}>
        <Box
          position={'absolute'}
          sx={{
            border: '1px solid',
            borderColor: '#7E7E7E33',
            boxShadow: '0px 4px 4px 0px #00000005',
            background: '#fff',
            zIndex: 4,
            mt: 1,
            borderRadius: 2,
            width: isGroup
              ? {xs: '100%', md: 550}
              : {xs: 'calc(100% - 40px)', md: 400},
            py: 1.5,
            pb: 2,
          }}
        >
          <Box
            width={'100%'}
            px={2}
            mb={2}
            sx={{
              backgroundColor: '#fff',
            }}
          >
            <TextField
              size="small"
              autoFocus
              placeholder={`Search ${props.label}`}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Image
                      src={SearchIcon}
                      alt="search"
                      width={12}
                      height={12}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                '.MuiInputBase-input': {
                  border: 'unset',
                  fontSize: 10,
                  paddingLeft: 0,
                },
              }}
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              variant={VersionVariants.VERSION_TWO}
            />
          </Box>
          <Box maxHeight={300} overflow={'scroll'}>
            <Grid container>
              {isGroup
                ? filteredOptions?.map((option: any, index: number) => (
                    <Grid item xs={12} key={`group-${index}`}>
                      <Typography variant="body3" pl={2} color={'#7E7E7ECC'}>
                        {option.label}
                      </Typography>
                      <Masonry spacing={0} columns={{xs: 1, md: 2}}>
                        {option.options?.length ? (
                          option.options.map((option: any, index: number) => (
                            <Box key={`select-v3-option-${index}`}>
                              <ListBox
                                data-value={option?.value}
                                onClick={(e: any) =>
                                  handleSelect(e, option?.value)
                                }
                              >
                                {props.multiple && (
                                  <Checkbox
                                    checked={SelectValue?.includes(
                                      option?.value
                                    )}
                                  />
                                )}
                                <Typography
                                  color={'text.primary'}
                                  fontSize={12}
                                  fontWeight={
                                    SelectValue?.includes(option?.value)
                                      ? 500
                                      : 400
                                  }
                                >
                                  {option.label}
                                </Typography>
                              </ListBox>
                            </Box>
                          ))
                        ) : (
                          <Box px={2}>
                            <Typography color={'#7E7E7ECC'} fontSize={12}>
                              No{' '}
                              {props.name ||
                                (props?.label as string)?.toLowerCase()}{' '}
                              found!
                            </Typography>
                          </Box>
                        )}
                      </Masonry>
                    </Grid>
                  ))
                : filteredOptions?.map((option: any, index: number) => (
                    <Grid item xs={12} key={`select-v3-option-${index}`}>
                      <ListBox
                        data-value={option?.value}
                        onClick={(e: any) => handleSelect(e, option?.value)}
                      >
                        {props.multiple && (
                          <Checkbox
                            checked={SelectValue?.includes(option?.value)}
                          />
                        )}
                        <Typography
                          color={'text.primary'}
                          fontSize={12}
                          fontWeight={
                            SelectValue?.includes(option?.value) ? 500 : 400
                          }
                        >
                          {option.label}
                        </Typography>
                      </ListBox>
                    </Grid>
                  ))}
            </Grid>
          </Box>
        </Box>
      </Transistion>
    </Box>
  );
};

export default Select;
